import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import KinAvatar from "@/components/KinAvatar";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import { useStatusBar } from "@/hooks/useStatusBar";
import { NETWORK_ERROR_MESSAGE, isAuthNetworkError, reportAuthNetworkError } from "@/lib/authNetworkError";
import { EMAIL_NOT_CONFIRMED_MESSAGE, isEmailNotConfirmedError } from "@/lib/emailNotConfirmed";
import { RESEND_COOLDOWN_SECONDS, resendConfirmationEmail } from "@/lib/resendConfirmation";

const SignIn = () => {
  useStatusBar({ color: 'F0F0FF', style: 'dark' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get("email") || "";
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // #1532 — when the account is unconfirmed she needs a new link, not a new
  // password. This is the tap-through #1531's copy promises.
  const [notConfirmed, setNotConfirmed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotConfirmed(false);
    setResendNotice(null);
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });

    if (error) {
      // A dropped connection is not a wrong password — telling her it is sends
      // her to reset a password that was fine. See lib/authNetworkError.ts.
      if (isAuthNetworkError(error)) {
        reportAuthNetworkError(error, "login");
        setError(NETWORK_ERROR_MESSAGE);
      } else if (isEmailNotConfirmedError(error)) {
        // #1531 — her password is FINE; the account was never confirmed. Saying
        // "incorrect password" here is what sent her to reset a working one.
        // Not reported to Sentry: this is an expected user state, not a failure.
        setError(EMAIL_NOT_CONFIRMED_MESSAGE);
        setNotConfirmed(true);
      } else {
        setError("Incorrect email or password");
      }
      setLoading(false);
      return;
    }
    navigate("/");
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  /**
   * She already typed her email into the form above, so this needs no second
   * input — one tap. resendConfirmationEmail READS the returned error and hands
   * back what to say; `resend` resolves its error rather than throwing, so a
   * try/catch here would be decorative (guardrail 16).
   */
  const handleResendConfirmation = async () => {
    setResending(true);
    setResendNotice(null);
    const outcome = await resendConfirmationEmail(email);
    setResending(false);
    setResendNotice(outcome.message);
    if (outcome.cooldown) setResendCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError("Enter your email above, then tap Forgot password");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        const msg = (error.message || "").toLowerCase();
        const status = (error as { status?: number }).status;
        if (status === 429 || msg.includes("rate limit") || msg.includes("too many")) {
          setError("You've requested a few of these recently — give it about an hour and try again 💜");
        } else if (status && status >= 500) {
          setError("Couldn't send the reset email right now. Try again in a minute?");
        } else {
          setError("Hmm, that didn't work. Mind double-checking your email?");
        }
        return;
      }
      setResetSent(true);
    } finally {
      setLoading(false);
    }
  };

  // Centered layout that still scrolls: #root is height:100dvh; overflow:hidden,
  // so the scroll container has to live here. Fixed height + overflow-y-auto on
  // the outer div, min-h-full on the inner centering wrapper — content shorter
  // than the viewport stays vertically centered, taller content scrolls instead
  // of clipping from both ends (issue #359).
  return (
    <div className="h-[100dvh] overflow-y-auto" style={{ backgroundColor: "#F0F0FF" }}>
      <div
        className="min-h-full flex flex-col items-center justify-center px-6 py-10"
        style={{ paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="w-full max-w-sm flex flex-col items-center">
          <div className="flex-shrink-0">
            <KinAvatar size="lg" variant="dark" />
          </div>

          <h1 className="font-display text-foreground text-center mt-5 mb-1" style={{ fontSize: "28px" }}>
            Welcome back, <span className="font-serif italic" style={{ color: "#5050C8" }}>mama.</span>
          </h1>

          {resetSent && (
            <p className="text-sm font-body text-center mt-2 mb-3" style={{ color: "#5050C8" }}>
              Reset link sent. Check your inbox 💜
            </p>
          )}

          <form onSubmit={handleSignIn} className="w-full mt-6 flex flex-col gap-4">
            <div>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full h-12 px-4 rounded-xl border font-body text-base bg-white focus:outline-none focus:ring-2 transition-all"
                style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
              />
            </div>
            <div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-12 px-4 rounded-xl border font-body text-base bg-white focus:outline-none focus:ring-2 transition-all"
                style={{ borderColor: "#C8C8F4", color: "#1C1A3E" }}
              />
            </div>

            {error && (
              <p className="text-sm font-body" style={{ color: "#B91C1C" }}>{error}</p>
            )}

            {notConfirmed && (
              <div>
                <button
                  type="button"
                  onClick={handleResendConfirmation}
                  disabled={resending || resendCooldown > 0}
                  data-testid="signin-resend"
                  className="w-full font-body font-semibold text-sm bg-transparent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ color: "#5050C8", minHeight: 44, border: "1.5px solid #C8C8F4", borderRadius: 14 }}
                >
                  {resending
                    ? "Sending…"
                    : resendCooldown > 0
                      ? `Send me a fresh link (${resendCooldown}s)`
                      : "Send me a fresh link"}
                </button>
                {resendNotice && (
                  <p className="text-sm font-body mt-2" style={{ color: "#5050C8" }}>{resendNotice}</p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 font-body font-semibold text-base text-white transition-all hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "#5050C8", borderRadius: "14px" }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <SocialAuthButtons />

          <button
            onClick={handleForgotPassword}
            className="mt-3 font-body text-sm bg-transparent border-none cursor-pointer"
            style={{ color: "#5050C8" }}
          >
            Forgot password?
          </button>

          <p className="mt-4 font-body text-sm" style={{ color: "#54525C" }}>
            Don't have an account?{" "}
            <Link to="/signup" className="font-semibold" style={{ color: "#5050C8" }}>
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
